import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, Trash, FolderOpen, CaretRight, Eye, MagnifyingGlass, CaretDown, FilePdf } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { TOOLS, PILLARS, PILLAR_ORDER, statusStyle, statusLabel, openPath, fmtDate } from "@/lib/tools";

const STATUS_FILTERS = [
  { key: "All", label: "All" },
  { key: "Draft", label: "Draft" },
  { key: "Active Execution", label: "Active" },
  { key: "Completed", label: "Completed" },
];

const PILLAR_FILTERS = [
  { key: "All", label: "All Pillars" },
  ...PILLAR_ORDER.map((p) => ({ key: p, label: PILLARS[p].label })),
];

export default function Library() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [pillarFilter, setPillarFilter] = useState("All");
  const [toolFilter, setToolFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/documents");
      setItems(res.data);
    } catch {
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setDeleting(true);
    try {
      await api.delete(`/documents/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Document deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const downloadPdf = async (d) => {
    if (downloadingId) return;
    setDownloadingId(d.id);
    try {
      const res = await api.get(`/documents/${d.id}/export.pdf`, { responseType: "blob" });
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `MODUS-${d.id}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  // tool dropdown options scoped to the active pillar
  const toolOptions = Object.values(TOOLS).filter((t) => pillarFilter === "All" || t.pillar === pillarFilter);
  const activeToolName = toolFilter === "All" ? "All" : (TOOLS[toolFilter]?.name || "All");

  const selectPillar = (key) => {
    setPillarFilter(key);
    // reset tool when the active pillar no longer contains it
    if (toolFilter !== "All" && key !== "All" && TOOLS[toolFilter]?.pillar !== key) setToolFilter("All");
  };

  const filtered = items.filter((i) =>
    (statusFilter === "All" || i.status === statusFilter) &&
    (pillarFilter === "All" || i.pillar === pillarFilter) &&
    (toolFilter === "All" || i.tool_type === toolFilter) &&
    (query.trim() === "" || (i.title || "").toLowerCase().includes(query.trim().toLowerCase()))
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto w-full py-10 px-6 lg:px-10">
        <p className="text-xs font-semibold tracking-[0.12em] uppercase text-slate-500">Master Archive</p>
        <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Library</h1>
        <p className="mt-2 text-slate-500">Every document across all tools and pillars, in one place.</p>

        {/* Top filter line — search · pillar segmented · tool dropdown */}
        <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-3">
          {/* Search (left) */}
          <div className="relative lg:w-72">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="library-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by Title…"
              className="h-10 w-full rounded-sm border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>

          {/* Pillar segmented control (center) */}
          <div className="inline-flex items-stretch rounded-sm border border-slate-200 overflow-hidden lg:mx-auto" data-testid="pillar-segmented">
            {PILLAR_FILTERS.map((f, idx) => (
              <button
                key={f.key}
                data-testid={`pillar-filter-${f.key.toLowerCase()}`}
                onClick={() => selectPillar(f.key)}
                className={`px-3.5 py-2 text-xs font-mono uppercase tracking-wide transition-colors ${idx > 0 ? "border-l border-slate-200" : ""} ${
                  pillarFilter === f.key ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Tool selector dropdown (right) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="tool-selector"
                className="h-10 inline-flex items-center justify-between gap-2 rounded-sm border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 lg:w-60"
              >
                <span className="truncate"><span className="text-slate-400">Filter by Tool:</span> {activeToolName}</span>
                <CaretDown size={14} className="text-slate-400 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm border-slate-200 bg-white max-h-80 overflow-y-auto w-60">
              <DropdownMenuItem data-testid="tool-option-all" onClick={() => setToolFilter("All")} className="rounded-sm text-sm cursor-pointer">
                All tools{pillarFilter !== "All" ? ` (${PILLARS[pillarFilter].label})` : ""}
              </DropdownMenuItem>
              {toolOptions.map((t) => (
                <DropdownMenuItem
                  key={t.type}
                  data-testid={`tool-option-${t.type}`}
                  onClick={() => setToolFilter(t.type)}
                  className="rounded-sm text-sm cursor-pointer"
                >
                  {t.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Status toggle row — tight uppercase mono set */}
        <div className="mt-3 inline-flex items-stretch rounded-sm border border-slate-200 overflow-hidden" data-testid="status-row">
          {STATUS_FILTERS.map((f, idx) => (
            <button
              key={f.key}
              data-testid={`status-filter-${f.label.toLowerCase()}`}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-wide transition-colors ${idx > 0 ? "border-l border-slate-200" : ""} ${
                statusFilter === f.key ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-slate-400">
              <CircleNotch size={28} className="animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 border border-dashed border-slate-300 rounded-sm bg-white">
              <FolderOpen size={40} weight="duotone" className="text-slate-300" />
              <p className="mt-3 text-slate-500">No documents in this view.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="library-table">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3 font-semibold">Title</th>
                    <th className="px-5 py-3 font-semibold hidden md:table-cell">Tool</th>
                    <th className="px-5 py-3 font-semibold hidden lg:table-cell">Pillar</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold hidden sm:table-cell">Created</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((d) => {
                    const pillar = PILLARS[d.pillar] || PILLARS.OPERATIONS;
                    return (
                      <tr key={d.id} data-testid={`library-row-${d.id}`} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3.5 cursor-pointer" onClick={() => navigate(openPath(d))}>
                          <span className="font-medium text-slate-900">{d.title}</span>
                          {d.views_count > 0 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid={`views-${d.id}`}>
                              <Eye size={12} /> {d.views_count}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-600 hidden md:table-cell">{d.tool_name}</td>
                        <td className="px-5 py-3.5 hidden lg:table-cell">
                          <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wide ${pillar.chip}`}>{pillar.label}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${statusStyle[d.status]}`}>{statusLabel(d.status)}</span>
                        </td>
                        <td className="px-5 py-3.5 font-mono text-xs text-slate-400 hidden sm:table-cell">{fmtDate(d.created_at)}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              data-testid={`open-${d.id}`}
                              onClick={() => navigate(openPath(d))}
                              className="p-2 text-slate-400 hover:text-slate-900 transition-colors"
                              title="Open"
                            >
                              <CaretRight size={16} />
                            </button>
                            <button
                              data-testid={`download-pdf-${d.id}`}
                              onClick={() => downloadPdf(d)}
                              disabled={downloadingId === d.id}
                              className="p-2 text-slate-400 hover:text-slate-900 transition-colors disabled:opacity-60"
                              title="Download PDF"
                            >
                              {downloadingId === d.id ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} />}
                            </button>
                            <button
                              data-testid={`delete-${d.id}`}
                              onClick={() => setPendingDelete(d)}
                              className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-medium text-slate-800">“{pendingDelete?.title}”</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={(e) => { e.preventDefault(); remove(); }}
              disabled={deleting}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
